import { Stack, SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import * as React from 'react';
import { Loader } from '../table-component/components/Loader';
import { AvatarLayout } from './components/AvatarLayout';
import { StandardLayout } from './components/StandardLayout';
import { ResultStatus } from '@dispatch/shared/types';
import { TabContainerProps } from '../tab-component/TabContainer';
import { ConfirmModal } from '../common';
import { StandardTabLayout } from './components/StandardTabLayout';
import { DrawerButton } from './components/DrawerButton';

export interface DrawerProps {
  title?: string;
  subtitle?: string;
  onClose: () => void;
  onSave?: () => void;
  onDeactivate?: () => void;
  onActivate?: () => void;
  saveDisabled?: boolean;
  /**
   * Not all pages allow for deactivation, this makes the deactivate button
   */
  showSaveButton?: boolean;
  showDeactivateButton?: boolean;
  showActivateButton?: boolean;
  /**
   * List of meta pairs to display in Header.
   *
   * { @see @dispath/ui/DrawerMeta }
   */
  metas?: React.ReactNode[];
  /**
   * Shows 'Active'/'Inactivate' Chip in top right corner
   */
  showIsActive?: boolean;
  /**
   * Value of active chip
   */
  isActive?: boolean;
  /**
   * Prefer viewport width units
   * @example 55vw
   */
  drawerWidth?: string;
  /**
   * Request status of the entity, controls loading state of drawer
   */
  status?: ResultStatus;
  /**
   * Image url is the url for the account profie image
   */
  imageUrl?: string;
  /**
   * saveButtonLabel allows to add a custom save button label. If no custom label is specified the button defaults to save.
   */
  saveButtonLabel?: string;
  /**
   * showButtons is a boolean value that will distinguish whether the drawer shows the save button or not. Default is true.
   */
  showButtons?: boolean;
  /**
   * sx allows custom styles to be added to the current drawer instance.
   */
  sx?: SxProps;
  /**
   * render tabs takes in a TabContainer, and sender the tabs to the header
   */
  renderTabs?: React.ReactElement<TabContainerProps>;
  /**
   * Optional content rendered to the right of the tabs (same row as renderTabs).
   * Only honored by the StandardTabLayout (i.e. when renderTabs is set and no imageUrl).
   */
  tabsRightSlot?: React.ReactNode;
  /**
   * confirmDeactivate forces save button to render a confirm dialogue model when pressing the action button in
   * the drawer footer. Only use one, and it will determine the buttons that are displayed.
   */
  confirmDeactivate?: boolean;
  confirmActivate?: boolean;
  /**
   * confirmContent is what renders to the body of the confirm modal. This can be a string or JSX
   */
  confirmContent?: string | React.ReactElement;
  /**
   * deactivateContent is what renders to the body of the confirm modal. This can be a string or JSX
   */
  deactivateContent?: string | React.ReactElement;
  deactivateDisabled?: boolean;
  deactivateVerbiage?: string;
  deactivateModalType?: 'deactivate' | 'cancelTask' | 'delete' | 'complete';
  activateContent?: string | React.ReactElement;
  renderRightSideButtons?: React.ReactNode[];
  renderLeftSideButtons?: React.ReactNode[];
  isStandby?: boolean;
}

export const DrawerComponent: React.FC<DrawerProps> = ({
  title,
  subtitle,
  children,
  onClose,
  saveDisabled,
  onSave,
  onDeactivate,
  onActivate,
  renderRightSideButtons,
  renderLeftSideButtons,
  showDeactivateButton,
  showActivateButton,
  showSaveButton = true,
  metas,
  isActive,
  showIsActive,
  drawerWidth = '45vw',
  status = 'success',
  imageUrl,
  showButtons = true,
  saveButtonLabel,
  sx,
  renderTabs,
  tabsRightSlot,
  confirmDeactivate = true,
  confirmActivate = true,
  confirmContent,
  deactivateContent,
  deactivateDisabled,
  deactivateVerbiage,
  deactivateModalType,
  activateContent,
  isStandby,
}) => {
  const [open, setOpen] = React.useState(true);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = React.useState(false);
  const [showActivateModal, setShowActivateModal] = React.useState(false);

  const onDrawerClose = React.useCallback(() => {
    if (isStandby || (!saveDisabled && showButtons)) {
      return setShowConfirmModal(true);
    }
    setOpen(false);
    onClose?.();
  }, [saveDisabled, setOpen, onClose]);

  const renderMeta = () => {
    if (status === 'loading') {
      return;
    }

    return metas?.map((m, i) => <React.Fragment key={i}>{m}</React.Fragment>);
  };

  const callSave = () => {
    onSave?.();
    setShowConfirmModal(false);
  };

  const headerLayout = () => {
    if (imageUrl) {
      return (
        <AvatarLayout
          imageUrl={imageUrl}
          title={title}
          subtitle={subtitle}
          showIsActive={showIsActive}
          isActive={isActive}
          renderMeta={renderMeta}
          metas={metas}
          renderTabs={renderTabs}
        />
      );
    } else if (renderTabs && !imageUrl) {
      return (
        <StandardTabLayout
          title={title}
          subtitle={subtitle}
          showIsActive={showIsActive}
          isActive={isActive}
          renderMeta={renderMeta}
          metas={metas}
          renderTabs={renderTabs}
          tabsRightSlot={tabsRightSlot}
        />
      );
    } else {
      return (
        <StandardLayout
          title={title}
          subtitle={subtitle}
          showIsActive={showIsActive}
          isActive={isActive}
          renderMeta={renderMeta}
          metas={metas}
        />
      );
    }
  };

  const renderHeader = () => (
    <Box
      id="header"
      data-testid="drawer-header"
      sx={(theme) => ({
        // In dark mode the header matches the nav chrome so the blade reads as
        // part of the dark theme; light mode keeps the brand indigo.
        backgroundColor:
          theme.palette.mode === 'dark' ? theme.palette.dark.main : '#303F9F',
        color: 'white',
      })}
    >
      {headerLayout()}
    </Box>
  );

  const renderChildren = () => (
    <Box
      id="content"
      data-testid="drawer-content"
      sx={{
        overflowY: 'auto',
        justifySelf: 'flex-start',
        flexGrow: 1,
        padding: '2em',
      }}
    >
      {status !== 'loading' && children}
      {status === 'loading' && <Loader data-testid="drawer-loader" />}
    </Box>
  );

  const renderSaveButton = () => {
    if (!showSaveButton) {
      return;
    }

    return (
      <Stack px={1}>
        <DrawerButton
          dataTestId="drawer-save"
          text={saveButtonLabel || 'Save'}
          onClick={callSave}
          isDisabled={status === 'loading' || saveDisabled || false}
          color={'info'}
          backgroundColor={'#2196F3'}
          variant={'contained'}
        />
      </Stack>
    );
  };

  const callDeactivate = (
    _: React.MouseEvent<HTMLButtonElement> | undefined,
    confirmed?: boolean | undefined
  ) => {
    if (confirmDeactivate && confirmed === undefined) {
      return setShowDeactivateModal(true);
    }
    onDeactivate?.();
    setShowDeactivateModal(false);
  };

  const callActivate = (
    _: React.MouseEvent<HTMLButtonElement> | undefined,
    confirmed?: boolean | undefined
  ) => {
    if (confirmActivate && confirmed === undefined) {
      return setShowActivateModal(true);
    }
    onActivate?.();
    setShowActivateModal(false);
  };

  const onConfirmDiscard = () => {
    setShowConfirmModal(false);
    setOpen(false);
    onClose?.();
  };

  const onDenyDiscard = () => {
    setShowConfirmModal(false);
  };

  const renderConfirmModal = () => {
    if (isStandby) {
      return (
        <ConfirmModal
          onConfirm={onConfirmDiscard}
          onDeny={onDenyDiscard}
          confirmContent="This resource is currently on standby. Are you sure you want to leave?"
          confirmTitle="Resource on Standby"
          modalType={'custom'}
          confirmButtonLabel="Yes"
          denyButtonLabel="No"
        />
      );
    }

    return (
      <ConfirmModal
        onConfirm={onConfirmDiscard}
        onDeny={onDenyDiscard}
        confirmContent={confirmContent}
      />
    );
  };

  const renderDeactivateModal = () => {
    return (
      <ConfirmModal
        onConfirm={() => callDeactivate(undefined, true)}
        onDeny={() => setShowDeactivateModal(false)}
        confirmContent={
          deactivateVerbiage || 'Do you really want to deactivate?'
        }
        confirmTitle={'Deactivate Confirmation'}
        modalType={deactivateModalType || 'deactivate'}
      />
    );
  };

  const renderActivateModal = () => {
    return (
      <ConfirmModal
        onConfirm={() => callActivate(undefined, true)}
        onDeny={() => setShowActivateModal(false)}
        confirmContent={activateContent || 'Do you really want to activate?'}
        confirmTitle={'Activate Confirmation'}
        modalType={'activate'}
      />
    );
  };

  const renderDeactivateButton = () => {
    if (!showDeactivateButton) {
      return;
    }

    return (
      <Stack px={1}>
        <DrawerButton
          dataTestId="drawer-deactivate"
          text={deactivateContent || 'Deactivate'}
          onClick={callDeactivate}
          isDisabled={deactivateDisabled || status === 'loading'}
          color={'error'}
          variant={'outlined'}
        />
      </Stack>
    );
  };

  const renderActivateButton = () => {
    if (!showActivateButton) {
      return;
    }

    return (
      <Stack px={1}>
        <DrawerButton
          dataTestId="drawer-activate"
          text={'Activate'}
          onClick={callActivate}
          isDisabled={status === 'loading'}
          color={'success'}
          variant={'outlined'}
        />
      </Stack>
    );
  };

  const renderFooter = () => {
    return (
      <Box
        id="footer"
        data-testid="drawer-footer"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : '#F4F5FA',
          minHeight: '3em',
        })}
      >
        {showButtons ? (
          <Stack
            justifyContent="center"
            direction="column"
            height="100%"
            px={1}
          >
            <Stack
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : '#F4F5FA',
              })}
              direction="row-reverse"
              justifyContent="space-between"
            >
              <Stack direction={'row-reverse'} justifyContent="space-between">
                {renderSaveButton()}
                {/*render extra buttons on right footer */}
                {renderRightSideButtons}
              </Stack>
              <Stack direction={'row-reverse'} justifyContent="space-between">
                {renderDeactivateButton()}
                {renderActivateButton()}
                {/*render extra buttons on left footer */}
                {renderLeftSideButtons}
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </Box>
    );
  };

  return (
    <Drawer
      data-testid="drawer-drawer"
      PaperProps={{
        sx: {
          minWidth: drawerWidth,
          maxHeight: '100vh',
          overflowY: 'clip',
          ...sx,
        },
      }}
      anchor={'right'}
      open={open}
      onClose={onDrawerClose}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="stretch"
        sx={{ minHeight: '100%' }}
      >
        {renderHeader()}
        {renderChildren()}
        {renderFooter()}
        {showConfirmModal && renderConfirmModal()}
        {showDeactivateModal && isActive && renderDeactivateModal()}
        {showActivateModal && !isActive && renderActivateModal()}
      </Stack>
    </Drawer>
  );
};
